import { useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useApiQuery, type Billing, type CheckoutSession, type PaymentMethod, type Plan, type Product } from 'grabbitbot-legacy-ui'
import { paymentClient, portalClient } from '../../api'
import { failureText } from '../../api/errorMessages'
import type { ChargeRequest, PlanQuote } from '../../api/types'
import { ConfirmButton } from '../admin/adminShared'
import { HelpLabel } from './HelpTip'
import { PURCHASE_HELP } from './purchaseHelp'
import {
  MAX_CREDIT_COUNT,
  MIN_CREDIT_COUNT,
  SUBSCRIPTION_STATUS_LABELS,
  checkoutCause,
  formatNumberN,
  hasUsablePaymentMethod,
  isQuoteUnavailable,
  isUsableQuote,
  moneyText,
  priceText,
  purchaseDecision,
  replaceablePlans,
  sameCharge,
  validateCreditCount,
  type PaymentFeedback,
  type usePaymentActions,
} from './paymentFlow'
import { hasPlanEnded, isCanceledButActive, planSessionCause, utcDateText } from './planStatus'

// Components ONLY (react-refresh/only-export-components) — the logic lives
// in `paymentFlow.ts`.

export type PaymentActions = ReturnType<typeof usePaymentActions>

const TABS: { to: string; label: string }[] = [
  { to: '/payment', label: 'Payment' },
  { to: '/payment/subscriptions', label: 'Subscriptions' },
  { to: '/payment/products', label: 'Products' },
  { to: '/payment/method', label: 'Payment method' },
  { to: '/payment/billing', label: 'Billing' },
  { to: '/payment/credit', label: 'Credits' },
  { to: '/payment/transactions', label: 'Transactions' },
  { to: '/payment/invoices', label: 'Invoices' },
]

/** Legacy `PaymentBoxHeaderMenuComponent` — the tab row on every payment page. */
export function PaymentTabs() {
  const { pathname } = useLocation()
  return (
    <nav className="portal-tabs" aria-label="Payment sections">
      {TABS.map((tab) => {
        const active = tab.to === '/payment' ? pathname === '/payment' : pathname === tab.to || pathname.startsWith(`${tab.to}/`)
        return (
          <Link
            key={tab.to}
            to={tab.to}
            aria-current={active ? 'page' : undefined}
            className={active ? 'portal-tab portal-tab--active' : 'portal-tab'}
          >
            {tab.label}
          </Link>
        )
      })}
    </nav>
  )
}

export function PaymentPage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="portal-page-title">
        <h1>{title}</h1>
      </div>
      <PaymentTabs />
      {children}
    </div>
  )
}

export function PaymentNotice({ feedback }: { feedback: PaymentFeedback | undefined }) {
  if (!feedback) return null
  return (
    <p
      role={feedback.tone === 'error' || feedback.tone === 'warning' ? 'alert' : 'status'}
      className={`portal-notice portal-notice--${feedback.tone}`}
    >
      {feedback.text}
    </p>
  )
}

/** A date in UTC with the zone written out (all dates of the payment pages
 * that matter for a decision are UTC, owner rule 2026-10-15). */
export function UtcDateTime({ value }: { value: string | null | undefined }) {
  if (!value) return <>—</>
  return <time dateTime={value}>{utcDateText(value)}</time>
}

/** Legacy `<app-datetime-display>` — local date + time, `—` when missing. */
export function DateTime({ value }: { value: string | null | undefined }) {
  if (!value) return <>—</>
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return <>{value}</>
  return <time dateTime={value}>{date.toLocaleString()}</time>
}

export function BillingInvoiceHint({ billing }: { billing: Billing | null | undefined }) {
  if (!billing || billing.line1) return null
  return (
    <p className="portal-hint">
      If you need an invoice, please set up your <Link to="/payment/billing">billing information</Link> before payment.
    </p>
  )
}

/** A saved-card charge behind an inline "are you sure" confirmation (legacy
 * `confirm('Are you sure you want to start this transaction?')`). After an
 * unknown outcome the label says it is a retry of the SAME payment (same
 * Idempotency-Key); after `charge_outcome_unknown` it stays disabled. */
export function ChargeButton({
  actions,
  actionKey,
  label,
  request,
  onDone,
  disabled,
}: {
  actions: PaymentActions
  actionKey: string
  label: string
  request: ChargeRequest | null
  onDone?: () => void
  disabled?: boolean
}) {
  const retry = actions.isRetry(actionKey)
  return (
    <ConfirmButton
      label={retry ? `Retry: ${label}` : label}
      confirmLabel="Yes, start the transaction"
      pendingText="Are you sure you want to start this transaction?"
      disabled={disabled || !request || actions.isBusy(actionKey) || actions.isLocked(actionKey)}
      onConfirm={() => {
        if (request) void actions.charge(actionKey, request, onDone)
      }}
    />
  )
}

export function CheckoutActionButton({
  actions,
  actionKey,
  label,
  create,
  disabled,
}: {
  actions: PaymentActions
  actionKey: string
  label: string
  create: (oldSessionId: string | undefined) => Promise<CheckoutSession>
  disabled?: boolean
}) {
  const busy = actions.isBusy(actionKey)
  return (
    <button type="button" disabled={disabled || busy} onClick={() => void actions.checkout(actionKey, create)}>
      {label}
    </button>
  )
}

function statusTone(status: number | null | undefined): string {
  if (status === 1) return 'portal-stat-badge portal-stat-badge--success'
  if (status === 6) return 'portal-stat-badge portal-stat-badge--accent'
  if (status === 3 || status === 9) return 'portal-stat-badge portal-stat-badge--danger'
  return 'portal-stat-badge'
}

/**
 * One plan with its subscription state and actions — the plan card of legacy
 * `PaymentComponent` (overview) and `PlanListComponent`, same branches:
 * not subscribed -> subscribe; active -> pay one month / cancel; unpaid (or
 * active but expired) -> pay debt; canceled -> resume (restart with a card on
 * file, else a new-card Checkout). Plus the "Unpaid fees" box.
 */
export function PlanCard({
  plan,
  method,
  billing,
  actions,
  onChanged,
  highlighted,
  allPlans,
}: {
  plan: Plan
  method: PaymentMethod | null
  billing: Billing | null
  actions: PaymentActions
  onChanged: () => void
  highlighted?: boolean
  /** Every plan on the page: the running bot plans among them can be
   * replaced by this one (plan switch, owner decision 2026-10-13). */
  allPlans?: Plan[] | null
}) {
  const sub = plan.subscription
  const status = sub?.status ?? 0
  const key = `plan:${plan.id ?? ''}`
  const usable = hasUsablePaymentMethod(method)
  const planId = plan.id ?? ''
  const request: ChargeRequest | null = planId ? { cause: 'manual', type: 'plan', plans: planId } : null
  const createSession = (oldSessionId: string | undefined) =>
    paymentClient.createPlanSession(method && method.setup ? 'pmchange' : 'setup', [planId], oldSessionId)

  const expired = hasPlanEnded(plan)
  // A cancelled plan whose paid period is over is bought again like a new
  // one: through the preview, balance first (0.15.0). Its old "Resume" button
  // sent the user to Checkout even when the balances covered it in full.
  const ended = status === 6 && expired
  const subscribeable = (Boolean(plan.price && plan.price > 0) && !sub) || (sub && sub.status === 0) || ended

  const onDone = () => {
    paymentClient.clearCache()
    onChanged()
  }

  return (
    <section className="portal-card" aria-label={`Plan ${plan.name ?? planId}`} data-highlighted={highlighted ? 'true' : undefined}>
      <div className="portal-section-title">
        <h2>{plan.name ?? planId}</h2>
        {sub && <span className={statusTone(status)}>{SUBSCRIPTION_STATUS_LABELS[status] ?? `Status ${status}`}</span>}
      </div>
      <p className="portal-plan-price">{priceText(plan.currency, plan.price)}</p>

      {status === 1 && (
        <p>
          Will be automatically renewed at <DateTime value={sub?.expiresAt} />
        </p>
      )}
      {isCanceledButActive(sub) && (
        <p className="portal-notice portal-notice--info" role="status">
          Canceled, active until <UtcDateTime value={sub?.expiresAt} />
        </p>
      )}
      {status === 6 && (
        <div className="portal-notice portal-notice--warning">
          You canceled your project and it will be stopped at <DateTime value={sub?.expiresAt} />, unless you restart it.
          {method && (!method.setup || method.status === 6) && (
            <p>To restart your subscription(s), please set up a new payment method.</p>
          )}
        </div>
      )}
      {status === 3 && (
        <div className="portal-notice portal-notice--error">
          We couldn&apos;t deduct the last fees automatically. Your project will be stopped at{' '}
          <DateTime value={sub?.expiresAt} />, unless you pay your fees.
          {sub?.lastFailAt && (
            <p>
              Last tried: <DateTime value={sub.lastFailAt} />
            </p>
          )}
        </div>
      )}

      {subscribeable && (
        <PlanPurchase
          plan={plan}
          replaceable={replaceablePlans(allPlans, plan)}
          method={method}
          actions={actions}
          actionKey={key}
          onDone={onDone}
          mode="subscribe"
          endedPeriod={Boolean(sub?.expiresAt)}
          legacyButtons={
            <div className="portal-btn-group">
              {usable ? (
                <>
                  <ChargeButton actions={actions} actionKey={key} label="Subscribe - Pay with current payment method" request={request} onDone={onDone} />
                  <CheckoutActionButton actions={actions} actionKey={key} label="Subscribe - Pay with and save new payment method" create={createSession} />
                </>
              ) : (
                <CheckoutActionButton actions={actions} actionKey={key} label="Subscribe - Pay with new payment method" create={createSession} />
              )}
            </div>
          }
        />
      )}
      {sub && status === 1 && !expired && (
        <PlanPurchase
          plan={plan}
          mode="renew"
          replaceable={[]}
          method={method}
          actions={actions}
          actionKey={key}
          onDone={onDone}
          endedPeriod={false}
          legacyButtons={
            <div className="portal-btn-group">
              {usable && <ChargeButton actions={actions} actionKey={key} label="Pay one month with current payment method" request={request} onDone={onDone} />}
              <CheckoutActionButton actions={actions} actionKey={key} label="Pay one month and save new payment method" create={createSession} />
            </div>
          }
        />
      )}
      {sub && (status === 3 || (status === 1 && expired)) && (
        <PlanPurchase
          plan={plan}
          mode="debt"
          replaceable={[]}
          method={method}
          actions={actions}
          actionKey={key}
          onDone={onDone}
          endedPeriod={expired}
          legacyButtons={
            <div className="portal-btn-group">
              {usable && <ChargeButton actions={actions} actionKey={key} label="Pay debt with current payment method" request={request} onDone={onDone} />}
              <CheckoutActionButton actions={actions} actionKey={key} label="Pay debt with and save new payment method" create={createSession} />
            </div>
          }
        />
      )}
      <div className="portal-btn-group">
        {sub && status === 1 && !expired && (
          <>
            <ConfirmButton
              label="Cancel subscription"
              confirmLabel="Yes, cancel it"
              pendingText="Are you sure you want to cancel this subscription?"
              disabled={actions.isBusy(`${key}:cancel`)}
              onConfirm={() => void actions.run(`${key}:cancel`, () => paymentClient.cancelSubscription(planId), onDone)}
            />
          </>
        )}
        {sub && status === 6 && !expired &&
          (usable ? (
            <button
              type="button"
              disabled={actions.isBusy(`${key}:restart`)}
              onClick={() => void actions.run(`${key}:restart`, () => paymentClient.restartSubscription(planId), onDone)}
            >
              Resume subscription
            </button>
          ) : (
            <CheckoutActionButton actions={actions} actionKey={key} label="Resume subscription and pay with new payment method" create={createSession} />
          ))}
      </div>
      <PaymentNotice feedback={actions.feedbackFor(key)} />
      <PaymentNotice feedback={actions.feedbackFor(`${key}:cancel`)} />
      <PaymentNotice feedback={actions.feedbackFor(`${key}:restart`)} />
      {(subscribeable || status === 3) && <BillingInvoiceHint billing={billing} />}

      {sub && status !== 0 && (
        <dl className="portal-kv">
          <dt>Unpaid fees</dt>
          <dd>
            {sub.unpaidFee ? (
              <strong className="portal-pnl-negative">{priceText(sub.currency, (sub.unpaidFee ?? 0) / 100)}</strong>
            ) : status === 6 ? (
              <>All fees has been paid, automatic renew disabled.</>
            ) : (
              <>All fees has been paid.</>
            )}
          </dd>
        </dl>
      )}
    </section>
  )
}

export type PurchaseMode = 'subscribe' | 'renew' | 'debt'

const PURCHASE_ENTRY_LABEL: Record<PurchaseMode, string> = {
  subscribe: 'Subscribe',
  renew: 'Pay one month',
  debt: 'Pay debt',
}

/**
 * Subscribing to a plan (owner decisions 2026-10-13, feesubscription 0.15.0):
 * the price is paid from the balances first (USDT, USDC, credit) and only
 * the remainder by card; a running bot plan can be REPLACED by this one.
 *
 * The quote (`GET /payment/quote`) is read first. When the payment service
 * has none yet (`quote_unavailable`), today's buttons are shown unchanged
 * (`legacyButtons`): no switch, no preview, no warning. Otherwise the user
 * picks "Subscribe" (nothing replaced) or "Switch from X to Y" (only the plan
 * they pick is replaced), sees the preview, and pays from it.
 */
function PlanPurchase({
  plan,
  replaceable,
  method,
  actions,
  actionKey,
  onDone,
  mode,
  endedPeriod = false,
  legacyButtons,
}: {
  plan: Plan
  /** subscribe: a plan not running (or ended); renew / debt: "Pay one month"
   * / "Pay debt" of a running plan (owner decision 2026-10-18): the same
   * preview, nothing replaced. */
  mode: PurchaseMode
  replaceable: Plan[]
  method: PaymentMethod | null
  actions: PaymentActions
  actionKey: string
  onDone: () => void
  /** The plan has an earlier (ended) period: Checkout must use `setup`, as
   * `pmchange` extends the OLD end date by a month instead of starting now
   * (`planSessionCause`). */
  endedPeriod?: boolean
  legacyButtons: ReactNode
}) {
  const planId = plan.id ?? ''
  const probe = useApiQuery(() => portalClient.quotePlan(planId), [planId])
  const [intent, setIntent] = useState<{ replaces: Plan | null } | null>(null)

  if (probe.loading) return <p className="portal-text-muted">Loading payment options…</p>
  if (probe.error && isQuoteUnavailable(probe.error)) return <>{legacyButtons}</>
  if (probe.error || !isUsableQuote(probe.data)) {
    return (
      <div>
        <p className="portal-notice portal-notice--error" role="alert">
          {probe.error ? failureText(probe.error, 'Could not load the payment preview') : 'Could not load the payment preview. Nothing was charged.'}
        </p>
        <button type="button" onClick={() => probe.refetch()}>
          Try again
        </button>
      </div>
    )
  }
  if (intent) {
    return (
      <PurchasePreview
        plan={plan}
        replaces={intent.replaces}
        alongside={intent.replaces ? [] : replaceable}
        method={method}
        actions={actions}
        actionKey={actionKey}
        onDone={onDone}
        mode={mode}
        endedPeriod={endedPeriod}
        onClose={() => setIntent(null)}
      />
    )
  }
  const name = plan.name ?? planId
  return (
    <div className="portal-btn-group">
      <button type="button" onClick={() => setIntent({ replaces: null })}>
        {PURCHASE_ENTRY_LABEL[mode]}
      </button>
      {replaceable.map((old) => (
        <button key={old.id} type="button" onClick={() => setIntent({ replaces: old })}>
          {`Switch from ${old.name ?? old.id} to ${name}`}
        </button>
      ))}
    </div>
  )
}

const QUOTE_CHANGED: PaymentFeedback = {
  tone: 'warning',
  text: 'The amounts changed since this preview. Please review them again; nothing was charged.',
}

/** The confirmation of one purchase: what is paid from which balance, the
 * card remainder, the credit before / after, and the next usage fee
 * warning. Every pay button re-reads the quote first and charges only when
 * it is still the same (`sameCharge`); otherwise the new figures are shown. */
function PurchasePreview({
  plan,
  replaces,
  alongside,
  method,
  actions,
  actionKey,
  onDone,
  mode,
  endedPeriod,
  onClose,
}: {
  plan: Plan
  replaces: Plan | null
  alongside: Plan[]
  method: PaymentMethod | null
  actions: PaymentActions
  actionKey: string
  onDone: () => void
  mode: PurchaseMode
  endedPeriod: boolean
  onClose: () => void
}) {
  const planId = plan.id ?? ''
  const replacesId = replaces?.id ?? undefined
  const loaded = useApiQuery(() => portalClient.quotePlan(planId, replacesId), [planId, replacesId])
  const [rechecked, setRechecked] = useState<PlanQuote | null>(null)
  const [notice, setNotice] = useState<PaymentFeedback | undefined>(undefined)
  const [checking, setChecking] = useState(false)
  const usable = hasUsablePaymentMethod(method)
  const name = plan.name ?? planId
  const oldName = replaces ? (replaces.name ?? replaces.id ?? '') : ''
  const title = replaces
    ? `Switch from ${oldName} to ${name}`
    : mode === 'renew'
      ? `Pay one month of ${name}`
      : mode === 'debt'
        ? `Pay the debt of ${name}`
        : `Subscribe to ${name}`

  if (loaded.loading) return <p className="portal-text-muted">Loading the payment preview…</p>
  const quote = rechecked ?? (isUsableQuote(loaded.data) ? loaded.data : null)
  if (!quote) {
    return (
      <section className="portal-purchase" aria-label="Payment preview">
        <h3>{title}</h3>
        <p className="portal-notice portal-notice--error" role="alert">
          {loaded.error ? failureText(loaded.error, 'Could not load the payment preview') : 'Could not load the payment preview. Nothing was charged.'}
        </p>
        <div className="portal-btn-group">
          <button type="button" onClick={() => loaded.refetch()}>
            Try again
          </button>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </section>
    )
  }

  const { payFromBalance, warning } = purchaseDecision(quote)
  const request: ChargeRequest = { cause: 'manual', type: 'plan', plans: planId, ...(replacesId ? { replacesPlan: replacesId } : {}) }
  const create = (oldSessionId: string | undefined) =>
    portalClient.createPlanSession(planSessionCause(method, endedPeriod), planId, { replacesPlan: replacesId, oldSessionId })

  /** Re-reads the quote; true only when it would still charge the same. */
  const stillSame = async (): Promise<boolean> => {
    setChecking(true)
    setNotice(undefined)
    try {
      const fresh = await portalClient.quotePlan(planId, replacesId)
      if (!isUsableQuote(fresh)) {
        setNotice({ tone: 'error', text: 'Could not re-check the price. Nothing was charged; please try again.' })
        return false
      }
      if (!sameCharge(fresh, quote)) {
        setRechecked(fresh)
        setNotice(QUOTE_CHANGED)
        return false
      }
      return true
    } catch (err) {
      setNotice({ tone: 'error', text: failureText(err, 'Could not re-check the price') })
      return false
    } finally {
      setChecking(false)
    }
  }
  const busy = checking || actions.isBusy(actionKey) || actions.isLocked(actionKey)
  const charge = async () => {
    if (await stillSame()) void actions.charge(actionKey, request, onDone)
  }
  const checkout = async () => {
    if (await stillSame()) void actions.checkout(actionKey, create)
  }
  const cardText = moneyText(quote.card, quote.currency)
  const retry = actions.isRetry(actionKey)

  return (
    <section className="portal-purchase" aria-label="Payment preview">
      <h3>{title}</h3>
      <dl className="portal-kv">
        <dt>
          <HelpLabel label="Plan fee" help={PURCHASE_HELP.fee} />
        </dt>
        <dd>{moneyText(quote.fee, quote.currency)}</dd>
        {replaces && (
          <>
            <dt>
              <HelpLabel label={`Credit for unused days of ${oldName}`} help={PURCHASE_HELP.proration} />
            </dt>
            <dd>{moneyText(quote.prorationCredit ?? '0.00', 'credit')}</dd>
          </>
        )}
        <dt>
          <HelpLabel label="Paid from your balances" help={PURCHASE_HELP.balanceCover} />
        </dt>
        <dd>
          {quote.balanceCover.length === 0
            ? 'Nothing'
            : quote.balanceCover.map((c) => <div key={c.currency}>{moneyText(c.amount, c.currency)}</div>)}
        </dd>
        <dt>
          <HelpLabel label="Card payment" help={PURCHASE_HELP.card} />
        </dt>
        <dd>
          <strong>{cardText}</strong>
        </dd>
        <dt>
          <HelpLabel label="Credit now" help={PURCHASE_HELP.creditBefore} />
        </dt>
        <dd>{moneyText(quote.creditBefore, 'credit')}</dd>
        <dt>
          <HelpLabel label="Credit after this payment" help={PURCHASE_HELP.creditAfter} />
        </dt>
        <dd>{moneyText(quote.creditAfter, 'credit')}</dd>
        <dt>
          <HelpLabel label="Next monthly usage fee (estimate)" help={PURCHASE_HELP.nextFee} />
        </dt>
        <dd>
          {quote.nextFeeEstimate === null || quote.nextFeeEstimate === undefined ? (
            'Cannot be estimated right now'
          ) : (
            <>at least {moneyText(quote.nextFeeEstimate, quote.nextFeeCurrency || 'credit')}</>
          )}
          {(quote.nextFeeOther ?? []).map((o) => (
            <div key={o.currency} className="portal-text-muted">
              plus {moneyText(o.amount, o.currency)}, taken from your {o.currency.toUpperCase()} balance
            </div>
          ))}
        </dd>
      </dl>

      {warning?.kind === 'below_next_fee' && (
        <p className="portal-notice portal-notice--warning" role="alert">
          <strong>Your credit will not cover the next usage fee.</strong> After this payment your credit will be{' '}
          {moneyText(warning.creditAfter, 'credit')}, less than the estimated next monthly usage fee (at least{' '}
          {moneyText(warning.estimate, warning.currency)}). The usage fee is taken from your credit on the 1st; if your
          credit is negative after the 7th of the month, your bot is stopped. You can <Link to="/payment/credit">buy credit</Link>{' '}
          before or after this payment.
        </p>
      )}
      {warning?.kind === 'fee_unknown' && (
        <p className="portal-notice portal-notice--warning" role="alert">
          <strong>The next monthly usage fee cannot be estimated right now.</strong> Make sure your credit covers it: the usage
          fee is taken from your credit on the 1st, and if your credit is negative after the 7th of the month, your bot is
          stopped.
        </p>
      )}
      {replaces && (
        <p className="portal-hint">
          {`${name} starts now and ${oldName} ends now; the unused days of ${oldName} are credited above. No other plan is changed.`}
        </p>
      )}
      {alongside.length > 0 && (
        <p className="portal-hint">
          {`${alongside.map((p) => p.name ?? p.id).join(', ')} keeps running alongside this plan. To replace it, close this and choose "Switch from …".`}
        </p>
      )}

      {!payFromBalance && (
        <p className="portal-hint">Your balances (USDT, then USDC, then credit) are used first; the card pays only the remainder.</p>
      )}
      <div className="portal-btn-group">
        {payFromBalance ? (
          <ConfirmButton
            label={retry ? 'Retry: Pay from balance' : 'Pay from balance'}
            confirmLabel="Yes, pay from my balances"
            pendingText={`Pay ${moneyText(quote.fee, quote.currency)} from your balances? No card is charged.`}
            disabled={busy}
            onConfirm={() => void charge()}
          />
        ) : (
          <>
            {usable && (
              <ConfirmButton
                label={retry ? `Retry: Pay ${cardText} with current payment method` : `Pay ${cardText} with current payment method`}
                confirmLabel="Yes, start the transaction"
                pendingText="Are you sure you want to start this transaction?"
                disabled={busy}
                onConfirm={() => void charge()}
              />
            )}
            <button type="button" disabled={busy} onClick={() => void checkout()}>
              {usable ? `Pay ${cardText} with and save new payment method` : `Pay ${cardText} with new payment method`}
            </button>
          </>
        )}
        <button type="button" onClick={onClose} disabled={busy}>
          Close
        </button>
      </div>
      <PaymentNotice feedback={notice} />
    </section>
  )
}

/** One-time product card (legacy overview / `ProductListComponent`). */
export function ProductCard({
  product,
  method,
  billing,
  actions,
  onChanged,
  highlighted,
}: {
  product: Product
  method: PaymentMethod | null
  billing: Billing | null
  actions: PaymentActions
  onChanged: () => void
  highlighted?: boolean
}) {
  const productId = product.id ?? ''
  const key = `product:${productId}`
  const status = product.onetimeproduct?.status ?? 0
  const usable = hasUsablePaymentMethod(method)
  const payable =
    Boolean(product.price && product.price > 0) && (product.limit === 0 || (product.onetimeproduct !== null && !product.onetimeproduct.status))
  const request: ChargeRequest | null = productId ? { cause: 'manual', type: 'product', products: productId } : null
  const create = (setup: boolean) => (oldSessionId: string | undefined) => {
    const c = checkoutCause(method, setup)
    return paymentClient.createProductSession(c.cause, [productId], { setupMethod: c.setupMethod, oldSessionId })
  }
  const onDone = () => {
    paymentClient.clearCache()
    onChanged()
  }

  return (
    <section className="portal-card" aria-label={`Product ${product.name ?? productId}`} data-highlighted={highlighted ? 'true' : undefined}>
      <div className="portal-section-title">
        <h2>{product.name ?? productId}</h2>
        {status === 1 && <span className="portal-stat-badge portal-stat-badge--success">Paid</span>}
        {status === 9 && <span className="portal-stat-badge portal-stat-badge--danger">Failed</span>}
      </div>
      <p className="portal-plan-price">
        {priceText(product.currency, product.price)} {status === 0 && <span className="portal-text-muted">(only once)</span>}
      </p>
      {status === 1 && (
        <p className="portal-pnl-positive">
          Paid successfully at <DateTime value={product.onetimeproduct?.payedAt} />
        </p>
      )}
      {payable && (
        <div className="portal-btn-group">
          {usable ? (
            <>
              <ChargeButton actions={actions} actionKey={key} label="Pay with current payment method" request={request} onDone={onDone} />
              <CheckoutActionButton actions={actions} actionKey={key} label="Pay with another payment method (don't change current payment method)" create={create(false)} />
              <CheckoutActionButton actions={actions} actionKey={key} label="Pay with and save new payment method" create={create(true)} />
            </>
          ) : (
            <CheckoutActionButton actions={actions} actionKey={key} label="Pay with new payment method" create={create(true)} />
          )}
        </div>
      )}
      <PaymentNotice feedback={actions.feedbackFor(key)} />
      {payable && <BillingInvoiceHint billing={billing} />}
    </section>
  )
}

/** Saved card summary (legacy `PaymentmethodInfoBlockComponent`). */
export function PaymentMethodSummary({ method }: { method: PaymentMethod | null }) {
  if (!method || !method.setup) {
    return <p className="portal-text-muted">No payment method on file.</p>
  }
  return (
    <div>
      <p>
        <strong>{(method.brand ?? 'Card').toUpperCase()}</strong> ending in {method.last4 ?? '****'}
        {method.expiresAt && (
          <>
            {' '}
            — expires {monthYear(method.expiresAt)}
          </>
        )}
      </p>
      {Boolean(method.expired) && <p className="portal-notice portal-notice--error">This card has expired. Please change your payment method.</p>}
      {!method.expired && Boolean(method.expiresIn7Days) && <p className="portal-notice portal-notice--warning">This card expires within 7 days.</p>}
      {!method.expired && !method.expiresIn7Days && Boolean(method.expiresIn30Days) && (
        <p className="portal-notice portal-notice--warning">This card expires within 30 days.</p>
      )}
      {Boolean(method.hasError) && <p className="portal-notice portal-notice--error">The last payment with this card failed.</p>}
    </div>
  )
}

/** Legacy `getMonthYear`: `MM/YY` in UTC. */
function monthYear(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  return `${month}/${String(date.getUTCFullYear()).substring(2)}`
}

/** Billing address summary (legacy `BillingInfoBlockComponent`). */
export function BillingSummary({ billing }: { billing: Billing | null }) {
  if (!billing) return <p className="portal-text-muted">No billing information.</p>
  const lines = [billing.name, billing.line1, billing.line2, [billing.postal_code, billing.city].filter(Boolean).join(' '), billing.state, billing.country]
    .filter((line) => line && String(line).trim())
  return (
    <div>
      {lines.length === 0 ? <p className="portal-text-muted">No billing address yet.</p> : lines.map((line, i) => <div key={i}>{line}</div>)}
      {billing.invoice_email && <div className="portal-text-muted">Invoices to: {billing.invoice_email}</div>}
      <p>
        <Link to="/payment/billing">Edit billing information</Link>
      </p>
    </div>
  )
}

export function CreditAmount({ balance, currency }: { balance: number | null | undefined; currency: string | null | undefined }) {
  const value = Number(balance ?? 0)
  const cls = value > 0 ? 'portal-pnl-positive' : value < 0 ? 'portal-pnl-negative' : undefined
  const unit = currency ?? 'credit'
  const plural = unit === 'credit' && (value < 0 || value > 1) ? 'credits' : unit
  return (
    <span className={cls}>
      {formatNumberN(value, 8)} {plural}
    </span>
  )
}

/** "Buy credits" (legacy overview credit card / `PaymentCreditViewComponent`):
 * a whole number of credits between 3 and 10000 (1 credit = 1 USD), kept as
 * the TYPED STRING all the way to the request. */
export function CreditBuyPanel({
  method,
  billing,
  actions,
  onChanged,
  saveNewMethodOnly,
}: {
  method: PaymentMethod | null
  billing: Billing | null
  actions: PaymentActions
  onChanged: () => void
  /** Payment-method change page: the only way offered is a Checkout that
   * saves the new method (legacy `PaymentmethodChangeComponent`). */
  saveNewMethodOnly?: boolean
}) {
  const [typed, setTyped] = useState(String(MIN_CREDIT_COUNT))
  const key = saveNewMethodOnly ? 'pmchange:credit' : 'credit:credit'
  const usable = hasUsablePaymentMethod(method)
  const valid = validateCreditCount(typed)
  const request: ChargeRequest | null = valid.ok ? { cause: 'manual', type: 'credit', creditCurrency: 'credit', credits: valid.value } : null
  const create = (setup: boolean) => (oldSessionId: string | undefined) => {
    const c = checkoutCause(method, setup)
    if (!valid.ok) return Promise.reject(new Error(valid.message))
    return paymentClient.createCreditSession(c.cause, 'credit', valid.value, { setupMethod: c.setupMethod, oldSessionId })
  }
  const onDone = () => {
    paymentClient.clearCache()
    onChanged()
  }
  return (
    <div className="portal-form">
      <label htmlFor="buy-credit-count">Buy credits:</label>
      <div className="portal-input-group">
        <input
          id="buy-credit-count"
          inputMode="numeric"
          value={typed}
          aria-invalid={!valid.ok}
          onChange={(e) => setTyped(e.target.value)}
        />
        <span className="portal-input-unit">credits</span>
      </div>
      <p className="portal-muted-small">
        {MIN_CREDIT_COUNT} – {MAX_CREDIT_COUNT}
      </p>
      {!valid.ok && <p className="portal-notice portal-notice--warning">{valid.message}</p>}
      <p>
        <strong>Total to pay:</strong> USD {valid.ok ? valid.value : '—'}
      </p>
      <div className="portal-btn-group">
        {saveNewMethodOnly ? (
          <CheckoutActionButton actions={actions} actionKey={key} label="Pay with and save new payment method" create={create(true)} disabled={!valid.ok} />
        ) : usable ? (
          <>
            <ChargeButton actions={actions} actionKey={key} label="Pay with current payment method" request={request} onDone={onDone} />
            <CheckoutActionButton actions={actions} actionKey={key} label="Pay with another payment method (don't change current payment method)" create={create(false)} disabled={!valid.ok} />
            <CheckoutActionButton actions={actions} actionKey={key} label="Pay with and save new payment method" create={create(true)} disabled={!valid.ok} />
          </>
        ) : (
          <CheckoutActionButton actions={actions} actionKey={key} label="Pay with new payment method" create={create(true)} disabled={!valid.ok} />
        )}
      </div>
      <PaymentNotice feedback={actions.feedbackFor(key)} />
      <BillingInvoiceHint billing={billing} />
    </div>
  )
}
